import { useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer, type View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, startOfDay, endOfDay, addDays } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { usePlannerInstructions, useCreateSowing } from "../hooks/useSowingLifecycle";
import type { CalendarInstructionDto, CalendarSowingEventDto } from "../services/sowingLifecycleApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { useNotification } from "@/shared/hooks/useNotification";

// ─── date-fns localizer for react-big-calendar ───────────────────────────────

const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: {
    sowingEvent?: CalendarSowingEventDto;
    instruction: CalendarInstructionDto;
    type: "sowing" | "window" | "planned";
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SowingCalendarPlannerPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>("week");

  // Compute date range for queries
  const { from, to } = useMemo(() => {
    const start = startOfMonth(subMonths(currentDate, 1));
    const end = endOfMonth(addMonths(currentDate, 1));
    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  }, [currentDate]);

  const instructionsQuery = usePlannerInstructions(from, to);
  const instructions = useMemo(
    () => instructionsQuery.data ?? [],
    [instructionsQuery.data],
  );

  // Dialog state
  const [selectedInstruction, setSelectedInstruction] = useState<CalendarInstructionDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [addSowingOpen, setAddSowingOpen] = useState(false);
  const [addSowingDate, setAddSowingDate] = useState("");

  // Build calendar events
  const events = useMemo<CalendarEvent[]>(() => {
    const result: CalendarEvent[] = [];

    for (const inst of instructions) {
      // Actual sowing children
      for (const sowing of inst.sowings) {
        const date = new Date(sowing.sowingDate);
        result.push({
          id: sowing.id,
          title: `${inst.variety ?? inst.instructionNumber} — ${sowing.traysSown} trays`,
          start: date,
          end: date,
          allDay: true,
          resource: { sowingEvent: sowing, instruction: inst, type: "sowing" },
        });
      }

      // Planned event from the instruction's scheduledSowingDate (when nothing is sown yet)
      if (inst.sowings.length === 0 && inst.scheduledSowingDate) {
        const date = new Date(inst.scheduledSowingDate);
        result.push({
          id: `planned-${inst.id}`,
          title: `${inst.variety ?? inst.instructionNumber} — ${inst.quantityToSow} trays (planned)`,
          start: date,
          end: date,
          allDay: true,
          resource: { instruction: inst, type: "planned" },
        });
      }

      // Optional window band (only for legacy instructions that carry a range)
      if (inst.scheduledStartDate && inst.scheduledEndDate) {
        result.push({
          id: `window-${inst.id}`,
          title: `[Window] ${inst.variety ?? inst.instructionNumber}`,
          start: startOfDay(new Date(inst.scheduledStartDate)),
          end: endOfDay(new Date(inst.scheduledEndDate)),
          allDay: true,
          resource: { instruction: inst, type: "window" },
        });
      }
    }

    return result;
  }, [instructions]);

  // Days covered by any instruction window — used to shade the calendar columns
  // so the window visually spans every day in its range, not just the start day.
  const windowDates = useMemo(() => {
    const set = new Set<string>();
    for (const inst of instructions) {
      if (!inst.scheduledStartDate || !inst.scheduledEndDate) continue;
      let cursor = startOfDay(new Date(inst.scheduledStartDate));
      const end = startOfDay(new Date(inst.scheduledEndDate));
      while (cursor <= end) {
        set.add(format(cursor, "yyyy-MM-dd"));
        cursor = addDays(cursor, 1);
      }
    }
    return set;
  }, [instructions]);

  const dayPropGetter = useCallback((date: Date) => {
    if (windowDates.has(format(date, "yyyy-MM-dd"))) {
      return {
        style: {
          backgroundColor: "color-mix(in oklch, var(--primary) 22%, transparent)",
        },
      };
    }
    return {};
  }, [windowDates]);

  // Event style
  const eventPropGetter = useCallback((event: CalendarEvent) => {
    if (event.resource.type === "window") {
      return {
        style: {
          backgroundColor: "hsl(var(--muted))",
          color: "hsl(var(--muted-foreground))",
          borderLeft: "3px solid hsl(var(--primary))",
          opacity: 0.6,
          fontSize: "0.75rem",
        },
      };
    }
    if (event.resource.type === "planned") {
      return {
        style: {
          backgroundColor: "transparent",
          color: "hsl(var(--primary))",
          border: "1px dashed hsl(var(--primary))",
          borderRadius: "4px",
          fontSize: "0.8rem",
        },
      };
    }
    return {
      style: {
        backgroundColor: "hsl(var(--primary))",
        color: "hsl(var(--primary-foreground))",
        borderRadius: "4px",
        fontSize: "0.8rem",
      },
    };
  }, []);

  // Click on an event → show instruction detail
  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedInstruction(event.resource.instruction);
    setDetailOpen(true);
  }, []);

  // Click on empty slot → open add sowing dialog for the nearest instruction
  const handleSelectSlot = useCallback(({ start }: { start: Date }) => {
    setAddSowingDate(format(start, "yyyy-MM-dd"));

    // Prefer instructions whose window covers this date
    let matching = instructions.filter((inst) => {
      if (inst.scheduledStartDate && inst.scheduledEndDate) {
        return new Date(inst.scheduledStartDate) <= start && new Date(inst.scheduledEndDate) >= start;
      }
      return false;
    });

    // Fallback: instructions whose scheduledSowingDate falls on this day
    if (matching.length === 0) {
      matching = instructions.filter((inst) =>
        inst.scheduledSowingDate && isSameDay(new Date(inst.scheduledSowingDate), start)
      );
    }

    if (matching.length >= 1) {
      setSelectedInstruction(matching[0]);
      setAddSowingOpen(true);
    }
  }, [instructions]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sowing Calendar Planner</h1>
          <p className="text-muted-foreground mt-1">
            View and plan sowing events across instruction windows. Click an event for details, or click an empty slot to add a sowing.
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {instructions.length} instructions in view
        </Badge>
      </div>

      <div className="rounded-lg border bg-background p-4" style={{ height: "calc(100vh - 200px)" }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={setView}
          date={currentDate}
          onNavigate={setCurrentDate}
          selectable
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          eventPropGetter={eventPropGetter}
          dayPropGetter={dayPropGetter}
          views={["month", "week", "day"]}
          popup
          tooltipAccessor={(e) => e.title}
        />
      </div>

      {/* Instruction Detail Dialog */}
      {selectedInstruction && (
        <InstructionDetailDialog
          instruction={selectedInstruction}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
      )}

      {/* Add Sowing Dialog (from slot click) */}
      {selectedInstruction && addSowingOpen && (
        <QuickAddSowingDialog
          instruction={selectedInstruction}
          initialDate={addSowingDate}
          open={addSowingOpen}
          onOpenChange={setAddSowingOpen}
        />
      )}
    </div>
  );
};

// ─── Instruction Detail Dialog ────────────────────────────────────────────────

function InstructionDetailDialog({
  instruction,
  open,
  onOpenChange,
}: {
  instruction: CalendarInstructionDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const remaining = instruction.quantityToSow - instruction.totalSown;
  const progress = instruction.quantityToSow > 0
    ? Math.round((instruction.totalSown / instruction.quantityToSow) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{instruction.instructionNumber}</DialogTitle>
          <DialogDescription>
            {instruction.variety} ({instruction.cropTypeName})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Target</p>
              <p className="font-semibold">{instruction.quantityToSow} trays</p>
            </div>
            <div>
              <p className="text-muted-foreground">Sown</p>
              <p className="font-semibold">{instruction.totalSown} trays ({progress}%)</p>
            </div>
            <div>
              <p className="text-muted-foreground">Remaining</p>
              <p className="font-semibold">{remaining} trays</p>
            </div>
            <div>
              <p className="text-muted-foreground">Status</p>
              <Badge variant="outline">{instruction.status}</Badge>
            </div>
            <div>
              <p className="text-muted-foreground">Window Start</p>
              <p>{instruction.scheduledStartDate ? format(new Date(instruction.scheduledStartDate), "dd MMM yyyy") : "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Window End</p>
              <p>{instruction.scheduledEndDate ? format(new Date(instruction.scheduledEndDate), "dd MMM yyyy") : "—"}</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>

          {/* Sowings list */}
          <div className="border-t pt-3">
            <p className="text-sm font-semibold mb-2">Sowing Events ({instruction.sowings.length})</p>
            {instruction.sowings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No sowings recorded yet</p>
            ) : (
              <div className="space-y-2">
                {instruction.sowings.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-sm border rounded p-2">
                    <div>
                      <span className="font-mono">{format(new Date(s.sowingDate), "dd MMM yyyy")}</span>
                      <span className="ml-2 text-muted-foreground">{s.traysSown} trays</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{s.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Quick Add Sowing Dialog (from empty slot click) ──────────────────────────

function QuickAddSowingDialog({
  instruction,
  initialDate,
  open,
  onOpenChange,
}: {
  instruction: CalendarInstructionDto;
  initialDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [formData, setFormData] = useState({
    sowingDate: initialDate,
    traysSown: "",
  });

  const createSowingMutation = useCreateSowing();
  const notify = useNotification();

  const remaining = instruction.quantityToSow - instruction.totalSown;

  const handleSubmit = () => {
    const trays = parseFloat(formData.traysSown);
    if (!formData.sowingDate || !trays || trays <= 0) {
      notify.error("Date and trays are required");
      return;
    }

    if (trays > remaining) {
      notify.error(`Cannot sow more than remaining (${remaining} trays)`);
      return;
    }

    // Validate date is within window
    if (instruction.scheduledStartDate && instruction.scheduledEndDate) {
      const date = new Date(formData.sowingDate);
      if (date < new Date(instruction.scheduledStartDate) || date > new Date(instruction.scheduledEndDate)) {
        notify.error("Sowing date must be within the instruction window");
        return;
      }
    }

    createSowingMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        sowingDate: new Date(formData.sowingDate).toISOString(),
        traysSown: trays,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          notify.success("Sowing added to calendar");
        },
        onError: (error: any) => {
          notify.error(error.response?.data?.message || "Failed to add sowing");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Sowing</DialogTitle>
          <DialogDescription>
            {instruction.variety} — Remaining: {remaining} trays
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>Date</FieldLabel>
            <DatePicker
              value={formData.sowingDate}
              onChange={(v) => setFormData((prev) => ({ ...prev, sowingDate: v }))}
              disabled={createSowingMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Trays to Sow <span className="text-destructive">*</span></FieldLabel>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={remaining}
              placeholder={`Max: ${remaining}`}
              value={formData.traysSown}
              onChange={(e) => setFormData((prev) => ({ ...prev, traysSown: e.target.value }))}
              disabled={createSowingMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={createSowingMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={createSowingMutation.isPending}>
              {createSowingMutation.isPending ? "Adding..." : "Add Sowing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
